import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/config/firebase';
import { getSevenDayCampaignResultsData, SevenDayCampaignData, SevenDayCampaignResult } from '@/services/pmt2SevenDayCampaignResultsService';

interface PlacementTransitionResultsDashboardProps {
  product: any | null;
}

interface PlacementRow {
  name: string;
  statusField?: string; // Only for actual campaigns
  campaignType: string; // For matching campaign results
  isTotal?: boolean;
  totalOf?: string[]; // For placement totals
  hasTransitionData?: boolean; // False for Exact and PTA
  startDateField?: string;
  onOffDaysField?: string;
  bidIncreaseDaysField?: string;
  afterPauseDaysField?: string;
  isBlankRow?: boolean; // For blank separator rows
}

// Row configuration matching the screenshot layout
const placementRows: PlacementRow[] = [
  {
    name: 'Exact',
    statusField: 'Exact Status',
    campaignType: 'Exact',
    hasTransitionData: false
  },
  {
    name: 'Placement Total',
    isTotal: true,
    totalOf: ['TOS Exact', 'ROS Exact', 'PP Exact'],
    campaignType: 'PLACEMENT_TOTAL_1'
  },
  {
    name: 'TOS Exact',
    statusField: 'TOS Exact Status',
    campaignType: 'TOS Exact',
    hasTransitionData: true,
    startDateField: 'TOS Exact Campaign Transition Start Date',
    onOffDaysField: 'TOS Exact Campaign On/Off Transition # of Days',
    bidIncreaseDaysField: 'TOS Exact Campaign Bid Increase Transition # of Days',
    afterPauseDaysField: 'Final After Pause Old Campaigns, Bid Increase Transition # of Days'
  },
  {
    name: 'ROS Exact',
    statusField: 'ROS Exact Status',
    campaignType: 'ROS Exact',
    hasTransitionData: true,
    startDateField: 'ROS Exact Campaign Transition Start Date',
    onOffDaysField: 'ROS Exact Campaign On/Off Transition # of Days',
    bidIncreaseDaysField: 'ROS Exact Campaign Bid Increase Transition # of Days',
    afterPauseDaysField: 'Final After Pause Old Campaigns, Bid Increase Transition # of Days'
  },
  {
    name: 'PP Exact',
    statusField: 'PP Exact Status',
    campaignType: 'PP Exact',
    hasTransitionData: true,
    startDateField: 'PP Exact Campaign Transition Start Date',
    onOffDaysField: 'PP Exact Campaign On/Off Transition # of Days',
    bidIncreaseDaysField: 'PP Exact Campaign Bid Increase Transition # of Days',
    afterPauseDaysField: 'Final After Pause Old Campaigns, Bid Increase Transition # of Days'
  },
  // Blank row to separate PTA section
  {
    name: '',
    campaignType: 'BLANK_ROW',
    hasTransitionData: false,
    isBlankRow: true
  },
  {
    name: 'PTA',
    statusField: 'PTA Status',
    campaignType: 'PTA',
    hasTransitionData: false
  },
  {
    name: 'Placement Total',
    isTotal: true,
    totalOf: ['TOS PTA', 'ROS PTA', 'PP PTA'],
    campaignType: 'PLACEMENT_TOTAL_2'
  },
  {
    name: 'TOS PTA',
    statusField: 'TOS PTA Status',
    campaignType: 'TOS PTA',
    hasTransitionData: true,
    startDateField: 'TOS PTA Campaign Transition Start Date',
    onOffDaysField: 'TOS PTA Campaign On/Off Transition # of Days',
    bidIncreaseDaysField: 'TOS PTA Campaign Bid Increase Transition # of Days',
    afterPauseDaysField: 'Final After Pause Old Campaigns, Bid Increase Transition # of Days'
  },
  {
    name: 'ROS PTA',
    statusField: 'ROS PTA Status',
    campaignType: 'ROS PTA',
    hasTransitionData: true,
    startDateField: 'ROS PTA Campaign Transition Start Date',
    onOffDaysField: 'ROS PTA Campaign On/Off Transition # of Days',
    bidIncreaseDaysField: 'ROS PTA Campaign Bid Increase Transition # of Days',
    afterPauseDaysField: 'Final After Pause Old Campaigns, Bid Increase Transition # of Days'
  },
  {
    name: 'PP PTA',
    statusField: 'PP PTA Status',
    campaignType: 'PP PTA',
    hasTransitionData: true,
    startDateField: 'PP PTA Campaign Transition Start Date',
    onOffDaysField: 'PP PTA Campaign On/Off Transition # of Days',
    bidIncreaseDaysField: 'PP PTA Campaign Bid Increase Transition # of Days',
    afterPauseDaysField: 'Final After Pause Old Campaigns, Bid Increase Transition # of Days'
  }
];

const PlacementTransitionResultsDashboard: React.FC<PlacementTransitionResultsDashboardProps> = ({ product }) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [statusData, setStatusData] = useState<Record<string, any> | null>(null);
  const [campaignResultsData, setCampaignResultsData] = useState<SevenDayCampaignData | null>(null);
  const [transitionData, setTransitionData] = useState<Record<string, any> | null>(null);

  // Fetch all data sources
  useEffect(() => {
    const fetchData = async () => {
      if (!product?.companyName || !product?.id) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        // Fetch status data, campaign results, and transition data in parallel
        const [statusResult, campaignResult, transitionResult] = await Promise.all([
          // Fetch status data from pmt2_data collection
          (async () => {
            const statusFields = [
              'Exact Status',
              'TOS Exact Status',
              'ROS Exact Status',
              'PP Exact Status',
              'PTA Status',
              'TOS PTA Status',
              'ROS PTA Status',
              'PP PTA Status'
            ];
            
            const statusPromises = statusFields.map(async (field) => {
              try {
                const docRef = doc(db, `pmt2_data/${product.companyName}/products/${product.id}`);
                const docSnap = await getDoc(docRef);
                return { field, value: docSnap.exists() ? docSnap.data()[field] : null };
              } catch (error) {
                console.error(`Error fetching status for ${field}:`, error);
                return { field, value: null };
              }
            });
            
            const results = await Promise.all(statusPromises);
            const statusMap: Record<string, any> = {};
            results.forEach(result => {
              statusMap[result.field] = result.value;
            });
            
            return statusMap;
          })(),
          
          // Fetch campaign results data
          getSevenDayCampaignResultsData(product.companyName, product.id),
          
          // Fetch transition data from pmt2_data collection
          (async () => {
            const transitionFields = [
              'TOS Exact Campaign Transition Start Date',
              'ROS Exact Campaign Transition Start Date',
              'PP Exact Campaign Transition Start Date',
              'TOS PTA Campaign Transition Start Date',
              'ROS PTA Campaign Transition Start Date',
              'PP PTA Campaign Transition Start Date',
              'TOS Exact Campaign On/Off Transition # of Days',
              'ROS Exact Campaign On/Off Transition # of Days',
              'PP Exact Campaign On/Off Transition # of Days',
              'TOS PTA Campaign On/Off Transition # of Days',
              'ROS PTA Campaign On/Off Transition # of Days',
              'PP PTA Campaign On/Off Transition # of Days',
              'TOS Exact Campaign Bid Increase Transition # of Days',
              'ROS Exact Campaign Bid Increase Transition # of Days',
              'PP Exact Campaign Bid Increase Transition # of Days',
              'TOS PTA Campaign Bid Increase Transition # of Days',
              'ROS PTA Campaign Bid Increase Transition # of Days',
              'PP PTA Campaign Bid Increase Transition # of Days',
              'Final After Pause Old Campaigns, Bid Increase Transition # of Days'
            ];
            
            const transitionPromises = transitionFields.map(async (field) => {
              try {
                const docRef = doc(db, `pmt2_data/${product.companyName}/products/${product.id}`);
                const docSnap = await getDoc(docRef);
                return { field, value: docSnap.exists() ? docSnap.data()[field] : null };
              } catch (error) {
                console.error(`Error fetching transition data for ${field}:`, error);
                return { field, value: null };
              }
            });
            
            const results = await Promise.all(transitionPromises);
            const transitionMap: Record<string, any> = {};
            results.forEach(result => {
              transitionMap[result.field] = result.value;
            });
            
            return transitionMap;
          })()
        ]);

        setStatusData(statusResult);
        setCampaignResultsData(campaignResult);
        setTransitionData(transitionResult);
      } catch (error) {
        console.error('Error fetching placement transition results data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [product]);

  // Helper functions
  const formatCurrency = (value: any): string => {
    const num = parseFloat(value);
    if (isNaN(num)) return '-';
    return `$${num.toFixed(0)}`;
  };

  const formatStatus = (value: any): string => {
    if (!value || value === '') return '-';
    return String(value);
  };

  const getStatusColor = (status: string): string => {
    const statusLower = status.toLowerCase();
    if (statusLower === 'enabled') {
      return 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100';
    } else if (statusLower === 'paused') {
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-100';
    }
    return '';
  };

  // Helper function to get campaign result data for a specific campaign
  const getCampaignResult = (campaignName: string): SevenDayCampaignResult | null => {
    if (!campaignResultsData?.campaigns) return null;
    return campaignResultsData.campaigns.find(c => c.campaignType === campaignName) || null;
  };

  // Helper functions to format campaign data
  const formatSpend = (campaignName: string): string => {
    const result = getCampaignResult(campaignName);
    return result && result.spend > 0 ? `$${Math.round(result.spend).toLocaleString()}` : '-';
  };

  const formatSales = (campaignName: string): string => {
    const result = getCampaignResult(campaignName);
    return result && result.sales > 0 ? `$${Math.round(result.sales).toLocaleString()}` : '-';
  };

  const formatACoS = (campaignName: string): string => {
    const result = getCampaignResult(campaignName);
    if (!result || result.spend <= 0 || result.sales <= 0) return '-';
    
    const acos = (result.spend / result.sales) * 100;
    return `${Math.round(acos)}%`;
  };

  const getACoSColor = (campaignName: string): string => {
    const result = getCampaignResult(campaignName);
    if (!result || result.spend <= 0 || result.sales <= 0 || !product) return '';
    
    const acos = (result.spend / result.sales) * 100;
    const finalTargetACoS = product['Final Target ACoS'];
    
    if (!finalTargetACoS || finalTargetACoS <= 0) return '';
    
    // Convert Final Target ACoS to percentage and multiply by 1.05
    const threshold = (finalTargetACoS * 100) * 1.05;
    
    if (acos > threshold) {
      return 'text-red-600 dark:text-red-400 font-semibold'; // Red for above threshold
    } else {
      return 'text-green-600 dark:text-green-400 font-semibold'; // Green for at or below threshold
    }
  };

  // Helper function to calculate placement totals
  const calculatePlacementTotal = (campaignNames: string[], type: 'spend' | 'sales'): number => {
    let total = 0;
    campaignNames.forEach(campaignName => {
      const result = getCampaignResult(campaignName);
      if (result) {
        total += type === 'spend' ? result.spend : result.sales;
      }
    });
    return total;
  };

  const formatPlacementTotalSpend = (campaignNames: string[]): string => {
    const total = calculatePlacementTotal(campaignNames, 'spend');
    return total > 0 ? `$${Math.round(total).toLocaleString()}` : '-';
  };

  const formatPlacementTotalSales = (campaignNames: string[]): string => {
    const total = calculatePlacementTotal(campaignNames, 'sales');
    return total > 0 ? `$${Math.round(total).toLocaleString()}` : '-';
  };

  const formatPlacementTotalACoS = (campaignNames: string[]): { value: string; color: string } => {
    const totalSpend = calculatePlacementTotal(campaignNames, 'spend');
    const totalSales = calculatePlacementTotal(campaignNames, 'sales');
    
    if (totalSpend <= 0 || totalSales <= 0) return { value: '-', color: '' };
    
    const acos = (totalSpend / totalSales) * 100;
    const finalTargetACoS = product?.['Final Target ACoS'];
    
    let color = '';
    if (finalTargetACoS && finalTargetACoS > 0) {
      const threshold = (finalTargetACoS * 100) * 1.05;
      if (acos > threshold) {
        color = 'text-red-600 dark:text-red-400 font-semibold';
      } else {
        color = 'text-green-600 dark:text-green-400 font-semibold';
      }
    }
    
    return { value: `${Math.round(acos)}%`, color };
  };

  // Helper function to get transition data
  const getTransitionData = (fieldName: string | undefined): string => {
    if (!fieldName || !transitionData) return '-';
    const value = transitionData[fieldName];
    return value ? String(value) : '-';
  };

  // Helper function to format date
  const formatDate = (dateValue: any): string => {
    if (!dateValue) return '-';
    try {
      const date = new Date(dateValue);
      // Check if the date is valid
      if (isNaN(date.getTime())) {
        return '-';
      }
      return date.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: '2-digit'
      });
    } catch {
      return '-';
    }
  };

  if (!product) {
    return (
      <div className="w-full">
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl font-bold">Placement Transition Results</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-500">Select a product to view Placement Transition Results.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full">
      <Card>
        <CardHeader className="p-4">
          <CardTitle className="text-2xl font-bold">Placement Transition Results</CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          {/* Table with frozen header */}
          <div className="w-full border rounded-lg overflow-hidden bg-white dark:bg-gray-900">
            <div className="overflow-auto">
              <table className="w-full border-collapse">
                {/* Header row - sticky */}
                <thead className="sticky top-0 z-20 bg-gray-100 dark:bg-gray-700">
                  <tr>
                    <th className="px-2 py-2 text-left text-xs text-gray-700 dark:text-gray-300 border-r border-b min-w-[120px]">
                      Campaign
                    </th>
                    <th className="px-1 py-2 text-center text-xs text-gray-700 dark:text-gray-300 border-r border-b min-w-[60px]">Status</th>
                    <th className="px-1 py-2 text-center text-xs text-gray-700 dark:text-gray-300 border-r border-b min-w-[80px]">
                      <div>Days Until</div>
                      <div>Pause</div>
                    </th>
                    <th className="px-1 py-2 text-center text-xs text-gray-700 dark:text-gray-300 border-r border-b min-w-[60px]">Spend</th>
                    <th className="px-1 py-2 text-center text-xs text-gray-700 dark:text-gray-300 border-r border-b min-w-[60px]">Sales</th>
                    <th className="px-1 py-2 text-center text-xs text-gray-700 dark:text-gray-300 border-r border-b min-w-[50px]">ACoS</th>
                    <th className="px-1 py-2 text-center text-xs text-gray-700 dark:text-gray-300 border-r border-b min-w-[70px]">
                      <div>Start</div>
                      <div>Date</div>
                    </th>
                    <th className="px-1 py-2 text-center text-xs text-gray-700 dark:text-gray-300 border-r border-b min-w-[80px]">
                      <div>On/Off #</div>
                      <div>of Days</div>
                    </th>
                    <th className="px-1 py-2 text-center text-xs text-gray-700 dark:text-gray-300 border-r border-b min-w-[80px]">
                      <div>Bid Increase</div>
                      <div># of Days</div>
                    </th>
                    <th className="px-1 py-2 text-center text-xs text-gray-700 dark:text-gray-300 border-b min-w-[100px]">
                      <div>After Pause, Bid</div>
                      <div>Increase # of Days</div>
                    </th>
                  </tr>
                </thead>

                {/* Data rows */}
                <tbody>
                  {placementRows.map((row, index) => {
                    const isPlacementTotal = row.isTotal;
                    const isBlankRow = row.isBlankRow;
                    const status = row.statusField && statusData ? formatStatus(statusData[row.statusField]) : '';
                    
                    return (
                      <tr key={`${row.name}-${index}`} className={`hover:bg-gray-100 dark:hover:bg-gray-600 ${isPlacementTotal ? 'bg-gray-50 dark:bg-gray-800 font-semibold' : ''}`}>
                        {/* Campaign Name */}
                        <td className="px-2 py-2 text-xs border-r border-b min-w-[120px]">
                          {!isBlankRow && (
                            <span className={`${isPlacementTotal ? 'font-bold' : 'font-semibold'} text-gray-900 dark:text-gray-100`}>
                              {row.name}
                            </span>
                          )}
                        </td>
                        
                        {/* Status */}
                        <td className="px-1 py-2 text-center text-xs border-r border-b min-w-[60px] whitespace-nowrap">
                          {isLoading ? (
                            <Skeleton className="h-4 w-12 mx-auto" />
                          ) : (
                            <>
                              {!isPlacementTotal && !isBlankRow && status && status !== '-' ? (
                                <span className={`px-1 py-0.5 rounded text-[10px] font-medium ${getStatusColor(status)}`}>
                                  {status}
                                </span>
                              ) : (
                                !isPlacementTotal && !isBlankRow && <span className="text-gray-400">-</span>
                              )}
                            </>
                          )}
                        </td>
                        
                        {/* Days Until Pause */}
                        <td className="px-1 py-2 text-center text-xs border-r border-b min-w-[80px] whitespace-nowrap">
                          {isLoading ? (
                            <Skeleton className="h-4 w-8 mx-auto" />
                          ) : (
                            !isPlacementTotal && !isBlankRow && <span className="text-gray-400">-</span>
                          )}
                        </td>
                        
                        {/* Spend */}
                        <td className="px-1 py-2 text-center text-xs border-r border-b min-w-[60px] whitespace-nowrap">
                          {isLoading ? (
                            <Skeleton className="h-4 w-12 mx-auto" />
                          ) : (
                            !isBlankRow && (
                              <span className="text-gray-900 dark:text-gray-100">
                                {isPlacementTotal && row.totalOf
                                  ? formatPlacementTotalSpend(row.totalOf)
                                  : formatSpend(row.campaignType)
                                }
                              </span>
                            )
                          )}
                        </td>
                        
                        {/* Sales */}
                        <td className="px-1 py-2 text-center text-xs border-r border-b min-w-[60px] whitespace-nowrap">
                          {isLoading ? (
                            <Skeleton className="h-4 w-12 mx-auto" />
                          ) : (
                            !isBlankRow && (
                              <span className="text-gray-900 dark:text-gray-100">
                                {isPlacementTotal && row.totalOf
                                  ? formatPlacementTotalSales(row.totalOf)
                                  : formatSales(row.campaignType)
                                }
                              </span>
                            )
                          )}
                        </td>
                        
                        {/* ACoS */}
                        <td className="px-1 py-2 text-center text-xs border-r border-b min-w-[50px] whitespace-nowrap">
                          {isLoading ? (
                            <Skeleton className="h-4 w-8 mx-auto" />
                          ) : (
                            !isBlankRow && (() => {
                              if (isPlacementTotal && row.totalOf) {
                                const totalACoS = formatPlacementTotalACoS(row.totalOf);
                                return (
                                  <span className={`${totalACoS.color || 'text-gray-900 dark:text-gray-100'}`}>
                                    {totalACoS.value}
                                  </span>
                                );
                              } else {
                                return (
                                  <span className={`${getACoSColor(row.campaignType) || 'text-gray-900 dark:text-gray-100'}`}>
                                    {formatACoS(row.campaignType)}
                                  </span>
                                );
                              }
                            })()
                          )}
                        </td>
                        
                        {/* Start Date */}
                        <td className="px-1 py-2 text-center text-xs border-r border-b min-w-[70px] whitespace-nowrap">
                          {isLoading ? (
                            <Skeleton className="h-4 w-12 mx-auto" />
                          ) : (
                            !isPlacementTotal && !isBlankRow && (
                              <span className="text-gray-900 dark:text-gray-100">
                                {row.hasTransitionData ? formatDate(getTransitionData(row.startDateField)) : '-'}
                              </span>
                            )
                          )}
                        </td>
                        
                        {/* On/Off # of Days */}
                        <td className="px-1 py-2 text-center text-xs border-r border-b min-w-[80px] whitespace-nowrap">
                          {isLoading ? (
                            <Skeleton className="h-4 w-8 mx-auto" />
                          ) : (
                            !isPlacementTotal && !isBlankRow && (
                              <span className="text-gray-900 dark:text-gray-100">
                                {row.hasTransitionData ? getTransitionData(row.onOffDaysField) : '-'}
                              </span>
                            )
                          )}
                        </td>
                        
                        {/* Bid Increase # of Days */}
                        <td className="px-1 py-2 text-center text-xs border-r border-b min-w-[80px] whitespace-nowrap">
                          {isLoading ? (
                            <Skeleton className="h-4 w-8 mx-auto" />
                          ) : (
                            !isPlacementTotal && !isBlankRow && (
                              <span className="text-gray-900 dark:text-gray-100">
                                {row.hasTransitionData ? getTransitionData(row.bidIncreaseDaysField) : '-'}
                              </span>
                            )
                          )}
                        </td>
                        
                        {/* After Pause, Bid Increase # of Days */}
                        <td className="px-1 py-2 text-center text-xs border-b min-w-[100px] whitespace-nowrap">
                          {isLoading ? (
                            <Skeleton className="h-4 w-8 mx-auto" />
                          ) : (
                            !isPlacementTotal && !isBlankRow && (
                              <span className="text-gray-900 dark:text-gray-100">
                                {row.hasTransitionData ? getTransitionData(row.afterPauseDaysField) : '-'}
                              </span>
                            )
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PlacementTransitionResultsDashboard;